import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import ConfirmModal from '../common/ConfirmModal';
import FormField from '../common/FormField';
import FormGrid from '../common/FormGrid';
import { useToast } from '../../contexts/ToastContext';
import {
  FaPlus, FaEdit, FaTrash, FaList, FaTh, FaSave,
  FaArrowsAlt, FaExpand, FaTable
} from 'react-icons/fa';
import { UtensilsCrossed } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import './CatalogoMesas.css';

const CatalogoMesas = () => {
  const toast = useToast();
  const mapRef = useRef(null);

  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vista, setVista] = useState('mapa'); // 'mapa' | 'lista'
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [seleccionada, setSeleccionada] = useState(null);
  const [aEliminar, setAEliminar] = useState(null);

  // Drag state
  const [dragging, setDragging] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Resize state
  const [resizing, setResizing] = useState(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, w: 0, h: 0 });

  const [form, setForm] = useState({
    numero: '', nombre: '', caption: '',
    posicionX: 0, posicionY: 0,
    width: 100, height: 80, negrita: '0'
  });

  // ═══════════════════════════════════════════════════════════════
  // API
  // ═══════════════════════════════════════════════════════════════
  const apiCall = useCallback(async (tipo, extra = {}) => {
    const res = await fetch(getApiUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tipo, ...extra })
    });
    return res.json();
  }, []);

  const cargarMesas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('getMesas');
      if (res.success) {
        setMesas(res.data || []);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al cargar mesas');
    } finally {
      setLoading(false);
    }
  }, [apiCall, toast]);

  useEffect(() => { cargarMesas(); }, [cargarMesas]);

  // ═══════════════════════════════════════════════════════════════
  // CRUD
  // ═══════════════════════════════════════════════════════════════
  const abrirNueva = () => {
    setSeleccionada(null);
    setForm({
      numero: '', nombre: '', caption: '',
      posicionX: 50, posicionY: 50,
      width: 100, height: 80, negrita: '0'
    });
    setShowModal(true);
  };

  const abrirEditar = (m) => {
    setSeleccionada(m);
    setForm({
      numero: m.numero || '',
      nombre: m.nombre || '',
      caption: m.caption || '',
      posicionX: m.posicionX || 0,
      posicionY: m.posicionY || 0,
      width: m.width || 100,
      height: m.height || 80,
      negrita: String(m.negrita) === '1' ? '1' : '0'
    });
    setShowModal(true);
  };

  const guardar = async () => {
    if (!form.caption && !form.nombre) {
      toast.error('El nombre o caption es obligatorio');
      return;
    }

    setGuardando(true);
    try {
      const tipo = seleccionada ? 'actualizarMesa' : 'crearMesa';
      const payload = { ...form };
      if (seleccionada) payload.idMesa = seleccionada.idMesa;

      const res = await apiCall(tipo, payload);
      if (res.success) {
        toast.success(res.message);
        setShowModal(false);
        cargarMesas();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al guardar mesa');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminar = (m) => {
    setAEliminar(m);
    setShowDeleteModal(true);
  };

  const eliminar = async () => {
    try {
      const res = await apiCall('eliminarMesa', { idMesa: aEliminar.idMesa });
      if (res.success) {
        toast.success(res.message);
        setShowDeleteModal(false);
        cargarMesas();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error('Error al eliminar mesa');
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // DRAG & DROP
  // ═══════════════════════════════════════════════════════════════
  const handleMouseDown = (e, mesa) => {
    if (e.button !== 0) return;
    // Check if clicking on resize handle
    if (e.target.classList.contains('cm-resize-handle')) return;

    e.preventDefault();
    const mapRect = mapRef.current.getBoundingClientRect();
    const scrollLeft = mapRef.current.scrollLeft;
    const scrollTop = mapRef.current.scrollTop;

    // posicionY = left, posicionX = top (matching MapaMesas.js convention)
    setDragOffset({
      x: e.clientX - mapRect.left + scrollLeft - (mesa.posicionY || 0),
      y: e.clientY - mapRect.top + scrollTop - (mesa.posicionX || 0)
    });
    setDragging(mesa.idMesa);
  };

  const handleMouseMove = useCallback((e) => {
    if (dragging !== null && mapRef.current) {
      const mapRect = mapRef.current.getBoundingClientRect();
      const scrollLeft = mapRef.current.scrollLeft;
      const scrollTop = mapRef.current.scrollTop;

      let newLeft = e.clientX - mapRect.left + scrollLeft - dragOffset.x;
      let newTop = e.clientY - mapRect.top + scrollTop - dragOffset.y;

      // Snap to grid (10px)
      newLeft = Math.round(newLeft / 10) * 10;
      newTop = Math.round(newTop / 10) * 10;

      // Clamp to positive
      newLeft = Math.max(0, newLeft);
      newTop = Math.max(0, newTop);

      setMesas(prev => prev.map(m =>
        m.idMesa === dragging ? { ...m, posicionY: newLeft, posicionX: newTop } : m
      ));
    }

    if (resizing !== null && mapRef.current) {
      const dx = e.clientX - resizeStart.x;
      const dy = e.clientY - resizeStart.y;
      let newW = Math.max(60, resizeStart.w + dx);
      let newH = Math.max(60, resizeStart.h + dy);

      // Snap to grid
      newW = Math.round(newW / 10) * 10;
      newH = Math.round(newH / 10) * 10;

      setMesas(prev => prev.map(m =>
        m.idMesa === resizing ? { ...m, width: newW, height: newH } : m
      ));
    }
  }, [dragging, dragOffset, resizing, resizeStart]);

  const handleMouseUp = useCallback(async () => {
    if (dragging !== null) {
      const mesa = mesas.find(m => m.idMesa === dragging);
      if (mesa) {
        try {
          await apiCall('actualizarPosicionMesa', {
            idMesa: mesa.idMesa,
            posicionX: mesa.posicionX,
            posicionY: mesa.posicionY
          });
        } catch (err) {
          console.error('Error guardando posicion:', err);
        }
      }
      setDragging(null);
    }

    if (resizing !== null) {
      const mesa = mesas.find(m => m.idMesa === resizing);
      if (mesa) {
        try {
          await apiCall('actualizarTamanoMesa', {
            idMesa: mesa.idMesa,
            width: mesa.width,
            height: mesa.height
          });
        } catch (err) {
          console.error('Error guardando tamano:', err);
        }
      }
      setResizing(null);
    }
  }, [dragging, resizing, mesas, apiCall]);

  const handleResizeStart = (e, mesa) => {
    e.preventDefault();
    e.stopPropagation();
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      w: mesa.width || 100,
      h: mesa.height || 80
    });
    setResizing(mesa.idMesa);
  };

  useEffect(() => {
    if (dragging !== null || resizing !== null) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragging, resizing, handleMouseMove, handleMouseUp]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="cm-container">
      <Header titulo="Catalogo de Mesas" color="#2196F3" />

      <div className="cm-content">
        {/* Toolbar */}
        <div className="cm-toolbar">
          <div className="cm-toolbar-left">
            <span className="cm-count"><FaTable /> {mesas.length} mesa(s)</span>
            <div className="cm-vista-toggle">
              <button
                className={`cm-vista-btn ${vista === 'mapa' ? 'cm-vista-btn--active' : ''}`}
                onClick={() => setVista('mapa')}
                title="Vista mapa"
              >
                <FaTh /> Mapa
              </button>
              <button
                className={`cm-vista-btn ${vista === 'lista' ? 'cm-vista-btn--active' : ''}`}
                onClick={() => setVista('lista')}
                title="Vista lista"
              >
                <FaList /> Lista
              </button>
            </div>
          </div>
          <button className="cm-btn cm-btn--primary" onClick={abrirNueva}>
            <FaPlus /> Nueva Mesa
          </button>
        </div>

        {loading ? (
          <div className="cm-loading">Cargando mesas...</div>
        ) : vista === 'mapa' ? (
          /* ───── VISTA MAPA ───── */
          <div className="cm-mapa-panel">
            <div className="cm-mapa-hint">
              <FaArrowsAlt /> Arrastra las mesas para moverlas &bull; <FaExpand /> Usa la esquina inferior derecha para redimensionar &bull; Doble click para editar
            </div>
            <div className="cm-mapa" ref={mapRef}>
              {mesas.map(mesa => {
                const w = Math.max(mesa.width || 100, 60);
                const h = Math.max(mesa.height || 80, 60);
                const isDragging = dragging === mesa.idMesa;
                const isResizing = resizing === mesa.idMesa;

                return (
                  <div
                    key={mesa.idMesa}
                    className={`cm-mesa-item ${isDragging ? 'cm-mesa-item--dragging' : ''} ${isResizing ? 'cm-mesa-item--resizing' : ''}`}
                    style={{
                      left: `${mesa.posicionY || 0}px`,
                      top: `${mesa.posicionX || 0}px`,
                      width: `${w}px`,
                      height: `${h}px`,
                      fontWeight: mesa.negrita === '1' ? 'bold' : 'normal',
                      zIndex: isDragging || isResizing ? 100 : 1
                    }}
                    onMouseDown={(e) => handleMouseDown(e, mesa)}
                    onDoubleClick={() => abrirEditar(mesa)}
                    title={`Mesa ${mesa.caption || mesa.numero} — Doble click para editar`}
                  >
                    <div className="cm-mesa-content">
                      <UtensilsCrossed size={14} />
                      <span className="cm-mesa-label">
                        {mesa.caption || mesa.nombre || `Mesa ${mesa.numero}`}
                      </span>
                      <span className="cm-mesa-num">#{mesa.numero}</span>
                    </div>
                    <div className="cm-mesa-actions-overlay">
                      <button
                        className="cm-mesa-action-btn cm-mesa-action-btn--edit"
                        onClick={(e) => { e.stopPropagation(); abrirEditar(mesa); }}
                        title="Editar"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="cm-mesa-action-btn cm-mesa-action-btn--delete"
                        onClick={(e) => { e.stopPropagation(); confirmarEliminar(mesa); }}
                        title="Eliminar"
                      >
                        <FaTrash />
                      </button>
                    </div>
                    <div
                      className="cm-resize-handle"
                      onMouseDown={(e) => handleResizeStart(e, mesa)}
                    />
                  </div>
                );
              })}
              {mesas.length === 0 && (
                <div className="cm-mapa-empty">
                  No hay mesas. Haz click en "Nueva Mesa" para agregar una.
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ───── VISTA LISTA ───── */
          <div className="cm-lista-panel">
            <table className="cm-tabla">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Caption</th>
                  <th>Posicion (X, Y)</th>
                  <th>Tamano (W x H)</th>
                  <th>Negrita</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {mesas.length === 0 ? (
                  <tr><td colSpan="7" className="cm-empty">No hay mesas</td></tr>
                ) : (
                  mesas.map(m => (
                    <tr key={m.idMesa}>
                      <td className="cm-td-num">{m.numero}</td>
                      <td>{m.nombre || '-'}</td>
                      <td><strong>{m.caption || '-'}</strong></td>
                      <td className="cm-td-pos">{m.posicionX}, {m.posicionY}</td>
                      <td className="cm-td-pos">{m.width} x {m.height}</td>
                      <td>{m.negrita === '1' ? 'Si' : 'No'}</td>
                      <td className="cm-td-actions">
                        <button className="cm-btn-icon cm-btn-icon--edit" onClick={() => abrirEditar(m)}><FaEdit /></button>
                        <button className="cm-btn-icon cm-btn-icon--delete" onClick={() => confirmarEliminar(m)}><FaTrash /></button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Footer />

      {/* ═══ MODAL CREAR/EDITAR ═══ */}
      <FormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={seleccionada ? 'Editar Mesa' : 'Nueva Mesa'}
        onSave={guardar}
        isSaving={guardando}
        saveLabel={seleccionada ? 'Actualizar' : 'Crear Mesa'}
        size="medium"
      >
        <FormGrid columns={2}>
          <FormField
            label="Numero de mesa"
            name="numero"
            type="number"
            value={form.numero}
            onChange={handleChange}
            placeholder="Auto si se deja vacio"
            min={1}
          />
          <FormField
            label="Caption (texto visible)"
            name="caption"
            value={form.caption}
            onChange={handleChange}
            required
            placeholder="Ej: Mesa 1, Terraza 3..."
          />
          <FormField
            label="Nombre"
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            placeholder="Nombre interno"
          />
          <FormField
            label="Negrita"
            name="negrita"
            type="select"
            value={form.negrita}
            onChange={handleChange}
            options={[
              { value: '0', label: 'No' },
              { value: '1', label: 'Si' }
            ]}
          />
        </FormGrid>

        <div className="cm-form-section">Posicion y Tamano</div>
        <FormGrid columns={4}>
          <FormField label="Pos. X (top)" name="posicionX" type="number" value={form.posicionX} onChange={handleChange} min={0} />
          <FormField label="Pos. Y (left)" name="posicionY" type="number" value={form.posicionY} onChange={handleChange} min={0} />
          <FormField label="Ancho" name="width" type="number" value={form.width} onChange={handleChange} min={60} />
          <FormField label="Alto" name="height" type="number" value={form.height} onChange={handleChange} min={60} />
        </FormGrid>
      </FormModal>

      {/* ═══ CONFIRM DELETE ═══ */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={eliminar}
        title="Eliminar Mesa"
        message={`Estas seguro de eliminar la mesa "${aEliminar?.caption || aEliminar?.nombre || aEliminar?.numero}"? Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
      />
    </div>
  );
};

export default CatalogoMesas;
